import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  IMyKnownError,
  IPaginatedListResponse,
  REQUEST_LIFE_CICLE,
} from 'interfaces/requests';
import { parseStrategyResponse, parseStrategyToSubmit } from './helpers';
import { IParsedStrategyToBFF, IStrategyResponse } from 'interfaces/Strategy';
import { post, MICROSERVICES, get, put } from 'api';
import endpoints from './endpoint';
import { openAlert } from 'modules/alertHandler';
import { setSelectedStrategyModeAction } from 'modules/strategyModeNavigation';
import { FORM_MODE } from 'shared/enums';
import { IStrategyRegister } from 'components/StrategyRegister/interfaces';
import { IStrategyRequestRequest } from './interfaces';
import { getStrategyErrorByTypeAndCode } from './messages';

interface IStrategyNavigationModeState {
  loading: REQUEST_LIFE_CICLE;
  current?: IStrategyRegister;
}

const initialState = {
  loading: REQUEST_LIFE_CICLE.IDLE,
  current: undefined,
} as IStrategyNavigationModeState;

export const loadStrategyAction = createAsyncThunk<
  IStrategyRegister,
  { id: string; mode: FORM_MODE },
  { rejectValue: IMyKnownError }
>('strategy/load', async ({ id, mode }, { rejectWithValue, dispatch }) => {
  try {
    const result = await get<IStrategyResponse>(MICROSERVICES.STRATEGIES, {
      url: endpoints.loadStrategy(id),
    });

    const parsedStrategy = parseStrategyResponse(result.data);

    ![FORM_MODE.list, FORM_MODE.listDetail].includes(mode) &&
      dispatch(setSelectedStrategyModeAction(mode));
    return mode === FORM_MODE.list ? initialState.current : parsedStrategy;
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: 'Ocorreu algum erro para carregar a Estratégia.',
      }),
    );
    console.error('error at load strategy', error);
    return rejectWithValue(error.response.data);
  }
});

export const createStrategyAction = createAsyncThunk<
  void,
  IStrategyRequestRequest,
  { rejectValue: IStrategyRegister }
>(
  'strategy/create',
  async ({ strategy, onSuccess, onFail }, { rejectWithValue, dispatch }) => {
    try {
      await post<IParsedStrategyToBFF>(MICROSERVICES.STRATEGIES, {
        url: endpoints.createStrategy(),
        data: parseStrategyToSubmit(strategy),
      });

      const successMessage =
        strategy.status === 'CREATED'
          ? 'Estratégia criada com sucesso!'
          : 'Rascunho da estratégia criado com sucesso';

      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: successMessage,
          primaryButton: 'Ok',
        }),
      );
      onSuccess && onSuccess();
      dispatch(setSelectedStrategyModeAction(FORM_MODE.list));
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content: getStrategyErrorByTypeAndCode(
            'CREATE',
            error.response.data.code,
          ),
        }),
      );
      onFail && onFail();
      console.error('error at create strategy', error);
      return rejectWithValue(strategy);
    }
  },
);

export const upadateStrategyAction = createAsyncThunk<
  void,
  IStrategyRequestRequest,
  { rejectValue: IStrategyRegister }
>(
  'strategy/update',
  async ({ strategy, onSuccess, onFail }, { rejectWithValue, dispatch }) => {
    try {
      await put<IParsedStrategyToBFF>(MICROSERVICES.STRATEGIES, {
        url: endpoints.updateStrategy(strategy.id),
        data: parseStrategyToSubmit(strategy),
      });

      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Estratégia atualizada com sucesso',
          primaryButton: 'Ok',
        }),
      );
      onSuccess && onSuccess();
      dispatch(setSelectedStrategyModeAction(FORM_MODE.list));
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content: getStrategyErrorByTypeAndCode(
            'UPDATE',
            error.response.data.code,
          ),
        }),
      );
      onFail && onFail();
      console.error('error at upadate strategy', error);
      return rejectWithValue(strategy);
    }
  },
);

export const activeStrategyAction = createAsyncThunk<
  void,
  IStrategyRequestRequest,
  { rejectValue: IMyKnownError }
>(
  'strategy/active',
  async ({ strategy, onSuccess, onFail }, { rejectWithValue, dispatch }) => {
    try {
      const resultActives = await get<
        IPaginatedListResponse<IStrategyRegister>
      >(MICROSERVICES.STRATEGIES, {
        url: endpoints.listStrategy(),
        params: {
          status: 'ACTIVE',
        },
      });

      dispatch(
        openAlert({
          title: 'Deseja realmente ativar a estratégia?',
          content:
            resultActives.data.count > 0
              ? 'Para ativar a estratégia atual será necessário deixar a anterior com status "Em finalização".'
              : '',
          primaryButton: 'Ativar',
          primaryButtonAction: upadateStrategyAction({
            strategy: { ...strategy, status: 'ACTIVE' },
            onSuccess,
            onFail,
          }),
          secondaryButton: 'Não',
        }),
      );
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const strategyModeSlice = createSlice({
  name: 'strategy',
  initialState,
  reducers: {
    resetStrategyRegisterAction(state) {
      state = Object.assign({}, initialState);
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createStrategyAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(createStrategyAction.rejected, (state, { payload }) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      state.current = { ...payload, status: state.current?.status };
      return state;
    });
    builder.addCase(createStrategyAction.fulfilled, (state) => {
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      state.current = undefined;
      return state;
    });

    builder.addCase(upadateStrategyAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(upadateStrategyAction.rejected, (state, { payload }) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      state.current = { ...payload, status: state.current?.status };
      return state;
    });
    builder.addCase(upadateStrategyAction.fulfilled, (state) => {
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      state.current = undefined;
      return state;
    });

    builder.addCase(loadStrategyAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(loadStrategyAction.rejected, (state) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      return state;
    });
    builder.addCase(loadStrategyAction.fulfilled, (state, { payload }) => {
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      state.current = payload;
      return state;
    });
  },
});
const { reducer, actions } = strategyModeSlice;
export const { resetStrategyRegisterAction } = actions;
export default reducer;
